import React from "react";
import {
  MapPin,
  User as UserIcon,
  Phone,
  GraduationCap,
  Clock,
  AlertTriangle,
  MessageCircle,
  CheckCircle2,
  XCircle,
  CalendarDays,
} from "lucide-react";
import { studentCalendarLabelWide } from "@/lib/utils-app";

/**
 * V1.35 · Barra lateral de contexto inteligente en Crear/Modificar Cita.
 *
 * Ley 1 (Existencia) — Sólo expone datos que ya existen en el backend.
 * Ley 2 (Contexto único) — El profesor decide sin salir del formulario.
 * Ley 3 (Acción) — Los avisos ofrecen sugerencias reservables clicables.
 *
 * Props:
 *   context        → payload de GET /api/students/{id}/appointment-context
 *   loading        → boolean
 *   suggestions    → array de {date, time, end_time, weekday}
 *   suggestLoading → boolean
 *   onApplySuggestion(s) → autocompleta fecha/hora inicio/hora fin
 *   teacher, duration → para el subtítulo de las sugerencias
 *   compact        → true en móvil para reducir paddings
 */

const WEEKDAY_LABELS = [
  { key: "monday",    short: "Lun" },
  { key: "tuesday",   short: "Mar" },
  { key: "wednesday", short: "Mié" },
  { key: "thursday",  short: "Jue" },
  { key: "friday",    short: "Vie" },
  { key: "saturday",  short: "Sáb" },
  { key: "sunday",    short: "Dom" },
];

const STATUS_LABEL = {
  activo: "Activo",
  pausado: "Pausado",
  baja: "Baja",
  candidato: "Candidato",
};

function formatUpdatedAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function formatDateES(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

function BonoChips({ bono }) {
  if (!bono) return null;
  const restColor =
    bono.Rest <= 0 ? "bg-red-100 text-red-800"
    : bono.Rest <= 2 ? "bg-amber-100 text-amber-800"
    : "bg-orange-100 text-orange-800";
  return (
    <div className="flex flex-wrap gap-1 text-[10px] font-mono tabular-nums" data-testid="ctx-bono">
      <span className="inline-flex items-center gap-0.5 rounded-md bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
        <span className="opacity-60">C:</span><span className="font-semibold">{bono.C}</span>
      </span>
      <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 px-1.5 py-0.5">
        <span className="opacity-60">R:</span><span className="font-semibold">{bono.R}</span>
      </span>
      <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50 text-amber-700 px-1.5 py-0.5">
        <span className="opacity-60">P:</span><span className="font-semibold">{bono.P}</span>
      </span>
      <span className="inline-flex items-center gap-0.5 rounded-md bg-red-50 text-red-700 px-1.5 py-0.5">
        <span className="opacity-60">NA:</span><span className="font-semibold">{bono.NA}</span>
      </span>
      <span className="inline-flex items-center gap-0.5 rounded-md bg-blue-50 text-blue-700 px-1.5 py-0.5">
        <span className="opacity-60">A:</span><span className="font-semibold">{bono.A >= 0 ? `+${bono.A}` : bono.A}</span>
      </span>
      <span className={"inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 " + restColor}>
        <span className="opacity-70">Rest:</span><span className="font-bold">{bono.Rest}</span>
      </span>
    </div>
  );
}

function ProximoExamenCard({ exam }) {
  if (!exam) return null;
  const label = exam.type === "examen_teorico" ? "Examen teórico"
              : exam.type === "examen_practico" ? "Examen práctico"
              : "Solicitud de examen";
  return (
    <div
      data-testid="ctx-proximo-examen"
      className="rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-0.5 flex items-center gap-1">
        <GraduationCap className="h-3 w-3" /> Próximo examen
      </div>
      <div className="font-semibold text-sm text-violet-900">{label}</div>
      <div className="text-xs text-violet-800 font-mono">
        {formatDateES(exam.date)}{exam.time ? ` · ${exam.time}` : ""}
        {exam.end_time ? `–${exam.end_time}` : ""}
      </div>
      {exam.centro && (
        <div className="text-[11px] text-violet-700 mt-0.5 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> {exam.centro}
        </div>
      )}
    </div>
  );
}

function IncidentsBlock({ incidents }) {
  if (!incidents || incidents.length === 0) return null;
  return (
    <div
      data-testid="ctx-incidents"
      className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 space-y-1"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-red-800 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" /> Incidencia activa
      </div>
      {incidents.map((i, idx) => (
        <div key={idx} className="text-xs text-red-900">
          <span className="font-semibold">{i.reason || "Sin motivo"}</span>{" "}
          <span className="font-mono text-red-700">({i.date_start} → {i.date_end})</span>
        </div>
      ))}
    </div>
  );
}

function WhatsAppLogsBlock({ logs }) {
  if (!logs || logs.length === 0) return null;
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  return (
    <div
      data-testid="ctx-whatsapp-logs"
      className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2 space-y-1"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 flex items-center gap-1">
        <MessageCircle className="h-3 w-3" /> Últimos envíos WhatsApp
      </div>
      {logs.map((l, i) => {
        const state = l.responded_at
          ? { label: "Respondido", color: "bg-emerald-600 text-white" }
          : l.opened_at
            ? { label: "Abierto", color: "bg-blue-500 text-white" }
            : { label: "Enviado", color: "bg-neutral-400 text-white" };
        return (
          <div
            key={i}
            data-testid={`ctx-whatsapp-log-${i}`}
            className="flex items-center justify-between gap-2 text-[10px]"
            title={
              (l.sent_at ? `Enviado: ${fmt(l.sent_at)}\n` : "") +
              (l.opened_at ? `Abierto: ${fmt(l.opened_at)}\n` : "") +
              (l.responded_at ? `Respondido: ${fmt(l.responded_at)}` : "")
            }
          >
            <span className="font-mono text-neutral-700 tabular-nums">
              {fmt(l.sent_at)}
            </span>
            <span className="text-neutral-600 whitespace-nowrap">
              {l.n_sessions || 0} {l.n_sessions === 1 ? "sesión" : "sesiones"}
            </span>
            <span className={`rounded-full px-1.5 py-0.5 font-semibold ${state.color}`}>
              {state.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}


function AvailabilityGrid({ availability }) {
  const weekly = availability?.weekly_slots || {};
  return (
    <div data-testid="ctx-availability">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Disponibilidad semanal
        </div>
        {availability?.updated_at && (
          <div
            className="text-[10px] text-neutral-500"
            data-testid="ctx-availability-updated"
            title={availability.updated_at}
          >
            {formatUpdatedAt(availability.updated_at)}
            {availability.updated_via ? ` · ${availability.updated_via}` : ""}
          </div>
        )}
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px]" data-testid="ctx-availability-grid">
        {WEEKDAY_LABELS.map((d) => {
          const slots = weekly[d.key] || [];
          const has = slots.some((s) => s.start && s.end);
          return (
            <div
              key={d.key}
              className={
                "rounded-md border px-1 py-1 " +
                (has ? "border-emerald-200 bg-emerald-50/60" : "border-neutral-200 bg-neutral-50")
              }
              data-testid={`ctx-availability-${d.key}`}
            >
              <div className="font-semibold text-neutral-700 mb-0.5 text-center">{d.short}</div>
              {has ? (
                slots.filter((s) => s.start && s.end).map((s, i) => (
                  <div key={i} className="font-mono text-emerald-800 leading-tight text-center text-[9px]">
                    {s.start}–{s.end}
                  </div>
                ))
              ) : (
                <div className="text-neutral-400 text-center">—</div>
              )}
            </div>
          );
        })}
      </div>
      {availability?.preferences && (
        <div className="mt-1.5 text-[11px] text-neutral-600">
          <span className="font-semibold">Preferencias:</span> {availability.preferences}
        </div>
      )}
    </div>
  );
}

// V1.52 · Listado vertical de sesiones del alumno (estilo listado WhatsApp):
// previas recientes + próximas, con los exámenes teórico/práctico resaltados.
function SessionsListBlock({ sesiones }) {
  const previas = sesiones?.previas || [];
  const proximas = sesiones?.proximas || [];
  if (previas.length === 0 && proximas.length === 0) return null;
  const TYPE_LABEL = {
    clase_practica: "Práctica",
    clase_teorica: "Teórica",
    examen_teorico: "EXAMEN TEÓRICO DGT",
    examen_practico: "EXAMEN PRÁCTICO DGT",
  };
  const isExam = (t) => (t || "").startsWith("examen");
  const Row = ({ a, past }) => {
    const exam = isExam(a.appointment_type);
    const teo = a.appointment_type === "examen_teorico";
    const failed = past && (a.exam_result === "no_apto" || (!exam && a.attended === false));
    const okPast = past && (a.exam_result === "apto" || (!exam && a.attended === true));
    return (
      <div
        data-testid={`ctx-session-${a.date}-${a.time}`}
        className={
          "flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] leading-tight " +
          (exam
            ? teo
              ? past ? "bg-blue-50/60 border border-blue-200" : "bg-blue-100 border border-blue-300"
              : past ? "bg-violet-50/60 border border-violet-200" : "bg-violet-100 border border-violet-300"
            : past ? "opacity-60" : "")
        }
      >
        {exam ? (
          <GraduationCap className={"h-3.5 w-3.5 shrink-0 " + (teo
            ? (past ? "text-blue-400" : "text-blue-700")
            : (past ? "text-violet-400" : "text-violet-700"))} />
        ) : past ? (
          failed
            ? <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
            : <CheckCircle2 className={"h-3.5 w-3.5 shrink-0 " + (okPast ? "text-emerald-600" : "text-neutral-400")} />
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
        )}
        <span className="font-mono tabular-nums font-semibold text-neutral-800 capitalize whitespace-nowrap">
          {formatDateES(a.date)}
        </span>
        <span className="font-mono tabular-nums text-neutral-600 whitespace-nowrap">
          {a.time}{a.end_time ? `–${a.end_time}` : ""}
        </span>
        <span className={
          "ml-auto truncate text-[10px] font-bold " +
          (exam ? (teo ? "text-blue-800" : "text-violet-800") : "text-neutral-500 font-semibold")
        }>
          {TYPE_LABEL[a.appointment_type] || a.appointment_type}
          {past && a.exam_result && (
            <span className={a.exam_result === "apto" ? " text-emerald-700" : " text-red-600"}>
              {" "}· {a.exam_result === "apto" ? "APTO" : a.exam_result === "no_apto" ? "NO APTO" : a.exam_result}
            </span>
          )}
        </span>
      </div>
    );
  };
  return (
    <div
      data-testid="ctx-sessions-list"
      className="rounded-lg border border-neutral-200 bg-neutral-50/60 px-2 py-2 space-y-1"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1 px-0.5">
        <CalendarDays className="h-3 w-3" /> Sesiones del alumno
      </div>
      {proximas.length > 0 && (
        <div className="space-y-0.5" data-testid="ctx-sessions-next">
          <div className="text-[9px] font-bold uppercase text-emerald-700 px-0.5">Próximas ({proximas.length})</div>
          {proximas.map((a, i) => <Row key={`n${i}`} a={a} past={false} />)}
        </div>
      )}
      {previas.length > 0 && (
        <div className="space-y-0.5 pt-1 border-t border-neutral-200" data-testid="ctx-sessions-prev">
          <div className="text-[9px] font-bold uppercase text-neutral-400 px-0.5">Anteriores ({previas.length})</div>
          {previas.map((a, i) => <Row key={`p${i}`} a={a} past={true} />)}
        </div>
      )}
    </div>
  );
}

function SuggestionsBlock({ suggestions, suggestLoading, onApplySuggestion, teacher, duration, hasStudent }) {
  return (
    <div data-testid="ctx-suggestions">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-600 mb-1">
        Próximos horarios compatibles
      </div>
      <div className="text-[10px] text-neutral-500 mb-1.5">
        Profesor: <span className="font-semibold">{teacher || "—"}</span>
        {" · "}Duración: <span className="font-semibold">{duration} min</span>
      </div>
      {!hasStudent ? (
        <div className="text-[11px] text-neutral-500">Selecciona un alumno para ver sugerencias.</div>
      ) : suggestLoading ? (
        <div className="text-[11px] text-neutral-500">Buscando huecos reservables…</div>
      ) : !suggestions || suggestions.length === 0 ? (
        <div
          data-testid="ctx-suggestions-empty"
          className="text-[11px] text-neutral-500"
        >
          No hay huecos libres en los próximos días dentro de la disponibilidad del alumno.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="ctx-suggestions-list">
          {suggestions.map((s, idx) => (
            <button
              key={`${s.date}-${s.time}-${idx}`}
              type="button"
              onClick={() => onApplySuggestion(s)}
              data-testid={`ctx-suggest-${idx}`}
              title="Aplicar este horario al formulario"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 text-[11px] font-semibold px-2.5 py-1 transition-colors"
            >
              <span className="font-mono">{formatDateES(s.date)}</span>
              <span className="opacity-60">·</span>
              <span className="font-mono">{s.time}–{s.end_time}</span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-1.5 text-[10px] text-neutral-500 leading-tight">
        Considera disponibilidad, agenda del profesor y exámenes ya asignados.
      </div>
    </div>
  );
}

export default function AppointmentContextSidebar({
  context,
  loading,
  suggestions,
  suggestLoading,
  onApplySuggestion,
  teacher,
  duration,
  hasStudent,
  compact = false,
}) {
  const pad = compact ? "p-2.5" : "p-3";
  const gap = compact ? "space-y-2.5" : "space-y-3";

  if (!hasStudent) {
    return (
      <aside
        className={"rounded-xl border border-neutral-200 bg-neutral-50 " + pad + " " + gap}
        data-testid="appointment-context-sidebar-empty"
      >
        <div className="text-[11px] text-neutral-500 text-center py-4">
          Selecciona un alumno para ver su ficha operativa.
        </div>
      </aside>
    );
  }

  if (loading || !context) {
    return (
      <aside
        className={"rounded-xl border border-neutral-200 bg-white " + pad + " " + gap}
        data-testid="appointment-context-sidebar-loading"
      >
        <div className="text-[11px] text-neutral-500">Cargando contexto del alumno…</div>
      </aside>
    );
  }

  const s = context.student || {};
  const label = studentCalendarLabelWide(s) || s.name || "—";
  const status = STATUS_LABEL[(s.status || "").toLowerCase()] || s.status || "";
  const phone = (s.phone || "").trim();
  const wa = phone ? `https://wa.me/${phone.replace(/[^\d]/g, "")}` : null;

  return (
    <aside
      className={"rounded-xl border border-neutral-200 bg-white " + pad + " " + gap}
      data-testid="appointment-context-sidebar"
    >
      {/* Cabecera */}
      <div>
        <div className="flex items-center gap-1.5 mb-0.5">
          <UserIcon className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
          <div className="font-bold text-sm leading-tight truncate" data-testid="ctx-student-name">
            {label}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-neutral-600">
          {status && (
            <span
              className={
                "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold " +
                ((s.status || "").toLowerCase() === "activo"
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-neutral-200 text-neutral-700")
              }
              data-testid="ctx-student-status"
            >
              {status}
            </span>
          )}
          {phone && (
            <a
              href={wa || undefined}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 text-[10px] font-semibold"
              data-testid="ctx-student-whatsapp"
              title={`WhatsApp ${phone}`}
            >
              <MessageCircle className="h-3 w-3" /> WA
            </a>
          )}
          {phone && (
            <span className="inline-flex items-center gap-1 text-neutral-500 font-mono">
              <Phone className="h-3 w-3" /> {phone}
            </span>
          )}
        </div>
        <div className="mt-1 grid grid-cols-2 gap-1 text-[10px]">
          {context.zona_habitual && (
            <div
              className="rounded-md bg-orange-50 border border-orange-200 px-2 py-1 truncate"
              data-testid="ctx-zona-habitual"
              title={`Zona habitual: ${context.zona_habitual}`}
            >
              <span className="opacity-60">Zona:</span>{" "}
              <span className="font-semibold text-orange-900">{context.zona_habitual}</span>
            </div>
          )}
          {context.profesor_habitual && (
            <div
              className="rounded-md bg-blue-50 border border-blue-200 px-2 py-1 truncate"
              data-testid="ctx-profesor-habitual"
              title={`Profesor habitual: ${context.profesor_habitual}`}
            >
              <span className="opacity-60">Profesor:</span>{" "}
              <span className="font-semibold text-blue-900">{context.profesor_habitual}</span>
            </div>
          )}
        </div>
      </div>

      <BonoChips bono={context.bono} />

      <IncidentsBlock incidents={context.incidents_active} />

      <WhatsAppLogsBlock logs={context.whatsapp_logs} />

      <ProximoExamenCard exam={context.proximo_examen} />

      <AvailabilityGrid availability={context.availability} />

      <SuggestionsBlock
        suggestions={suggestions}
        suggestLoading={suggestLoading}
        onApplySuggestion={onApplySuggestion}
        teacher={teacher}
        duration={duration}
        hasStudent={hasStudent}
      />

      <SessionsListBlock sesiones={context.sesiones} />
    </aside>
  );
}
